import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpUtilsService } from "./http-utils.service";
import { Observable, catchError, map, mergeMap, of } from "rxjs";
import { JsonConvert } from "json2typescript";
import { environment } from "../../../environment/environment";
import { CountersListData } from "../../model/cashback/counters";
import { Paging } from "../../model/paging/paging";
import {
  Client,
  ClientsData,
  ClientsListData,
} from "../../model/client/client";
import {
  ClientsCounters,
  ClientsCountersData,
} from "../../model/client/counters";
import {
  ClientSession,
  ClientSessionListData,
} from "../../model/client/session";
@Injectable({
  providedIn: "root",
})
export class ClientService {
  private _url: string = `${environment.baseAPI}/locations-groups/engagement`;
  constructor(
    private _http: HttpClient,
    private _httpUtils: HttpUtilsService,
  ) {}

  /**
   * Retrieves a list of clients based on the specified parameters.
   *
   * @param since - The start date for filtering clients.
   * @param until - The end date for filtering clients.
   * @param paging - The paging configuration for the API request.
   * @param mkt_consent - Optional. Indicates whether marketing consent is required.
   * @param prf_consent - Optional. Indicates whether profile consent is required.
   * @param location_id - Optional. The ID of the location to filter clients by.
   * @returns An Observable that emits the list of clients or an error.
   * @throws Throws an error if an exception occurs during the API request.
   */
  GETClients(
    since: string,
    until: string,
    paging: Paging,
    mkt_consent?: boolean,
    prf_consent?: boolean,
    location_id?: string,
  ): Observable<Client | any> {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http
            .get(
              `${this._url}/users${paging.getQs()}&since=${since}&until=${until}${location_id ? `&location_id=${location_id}` : ""}${mkt_consent ? `&mkt_consent=${mkt_consent}` : ""}${prf_consent ? `&prf_consent=${prf_consent}` : ""}`,
              { headers: httpHeaders },
            )
            .pipe(
              map(
                (res: any) => {
                  const jsonConvert: JsonConvert = new JsonConvert();
                  const response: ClientsListData =
                    jsonConvert.deserializeObject(res, ClientsListData);
                  if (response.success) {
                    return response;
                  } else {
                    this._httpUtils.handleError(response);
                    return;
                  }
                },
                catchError((err) => {
                  if (err.status === 401) {
                    // Unauthorized
                    this._httpUtils.logout();
                  }
                  throw err;
                }),
              ),
            );
        }),
        catchError((err) => {
          throw err;
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retrieves client data by client ID.
   *
   * @param {string} client_id - The ID of the client to retrieve.
   * @returns {Observable<Client | any>} An observable containing the client data or an error.
   *
   * @throws Will throw an error if the HTTP request fails.
   *
   * This method performs the following steps:
   * 1. Retrieves HTTP headers asynchronously.
   * 2. Makes an HTTP GET request to fetch client data.
   * 3. Deserializes the response into a `ClientsData` object.
   * 4. Checks if the response indicates success and returns the data.
   * 5. Handles errors, including unauthorized access by logging out the user.
   */
  GETClient(client_id: string): Observable<Client | any> {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http
            .get(`${this._url}/users/${client_id}`, { headers: httpHeaders })
            .pipe(
              map(
                (res: any) => {
                  const jsonConvert: JsonConvert = new JsonConvert();
                  const response: ClientsData = jsonConvert.deserializeObject(
                    res,
                    ClientsData,
                  );
                  if (response.success) {
                    return response;
                  } else {
                    this._httpUtils.handleError(response);
                    return;
                  }
                },
                catchError((err) => {
                  if (err.status === 401) {
                    // Unauthorized
                    this._httpUtils.logout();
                  }
                  throw err;
                }),
              ),
            );
        }),
        catchError((err) => {
          throw err;
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retrieves the count of clients within a specified date range and location.
   *
   * @param {string} [since] - The start date for the count in ISO format (optional).
   * @param {string} [until] - The end date for the count in ISO format (optional).
   * @param {string} [location_id] - The ID of the location to filter the count (optional).
   * @returns {Observable<ClientsCounters | any>} An observable containing the clients count data or an error.
   * @throws Will throw an error if the HTTP request fails or if the response indicates a failure.
   */
  GETClientsCount(
    since?: string,
    until?: string,
    location_id?: string,
  ): Observable<ClientsCounters | any> {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http
            .get(
              `${this._url}/users/counters?${location_id ? `location_id=${location_id}` : ""}${since ? `&since=${since}` : ""}${until ? `&until=${until}` : ""}`,
              { headers: httpHeaders },
            )
            .pipe(
              map(
                (res: any) => {
                  const jsonConvert: JsonConvert = new JsonConvert();
                  const response: ClientsCountersData =
                    jsonConvert.deserializeObject(res, ClientsCountersData);
                  if (response.success) {
                    return response;
                  } else {
                    this._httpUtils.handleError(response);
                    return;
                  }
                },
                catchError((err) => {
                  if (err.status === 401) {
                    // Unauthorized
                    this._httpUtils.logout();
                  }
                  throw err;
                }),
              ),
            );
        }),
        catchError((err) => {
          throw err;
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retrieves client sessions based on the provided parameters.
   *
   * @param {Paging} paging - The paging information for the request.
   * @param {string} client_id - The ID of the client whose sessions are being retrieved.
   * @param {string} [time_range] - Optional time range for filtering sessions.
   * @param {string} [since] - Optional start date for filtering sessions.
   * @param {string} [until] - Optional end date for filtering sessions.
   * @returns {Observable<ClientSession | any>} An observable containing the client session data or an error.
   * @throws Will throw an error if the request fails.
   */
  GETClientSessions(
    paging: Paging,
    client_id: string,
    time_range?: string,
    since?: string,
    until?: string,
  ): Observable<ClientSession | any> {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http
            .get(
              `${this._url}/users/${client_id}/sessions${paging.getQs()}&time_range=${time_range}&since=${since}&until=${until}`,
              { headers: httpHeaders },
            )
            .pipe(
              map(
                (res: any) => {
                  const jsonConvert: JsonConvert = new JsonConvert();
                  const response: ClientSessionListData =
                    jsonConvert.deserializeObject(res, ClientSessionListData);
                  if (response.success) {
                    return response;
                  } else {
                    this._httpUtils.handleError(response);
                    return;
                  }
                },
                catchError((err) => {
                  if (err.status === 401) {
                    // Unauthorized
                    this._httpUtils.logout();
                  }
                  throw err;
                }),
              ),
            );
        }),
        catchError((err) => {
          throw err;
        }),
      );
    } catch (error) {
      throw error;
    }
  }
}
